import { useTranslation } from 'next-i18next';
import { Dispatch, SetStateAction, memo, useCallback, useState } from 'react';
import { cn, Flex, Text } from '@bridebook/aura';
import { Link } from '@bridebook/web/app-shared/components/link/link';
import { useMarket } from '@bridebook/web/app-shared/lib/i18n/hooks/use-market';
import { UrlHelper } from '@bridebook/web/lib/url-helper';
import SearchBar from '@bridebook/web/app-shared/components/articles/search-bar/search-bar';
import { getArticleCategoriesList } from '@bridebook/web/lib/articles/constants/articleCategories';
import { useSelector } from '@bridebook/web/app-basic/lib/utils';
import { getIsMobile } from '@bridebook/web/app-basic/lib/app/selectors';

interface IProps {
  isBookmarksView?: boolean;
  activeSlug?: string;
  setCurrentPage: Dispatch<SetStateAction<number>>;
}

const ArticleCategoriesList = ({ isBookmarksView, activeSlug, setCurrentPage }: IProps) => {
  const { t } = useTranslation('articles');
  const market = useMarket();
  const [tagChecked, setTagChecked] = useState('');

  const isMobile = useSelector(getIsMobile);

  const onTagClick = useCallback(
    (value: string) => (): void => {
      if (value === tagChecked) return;
      setTagChecked(value);
      setCurrentPage(1);
    },
    [setCurrentPage, tagChecked],
  );

  const categories = getArticleCategoriesList();
  const showCategories = categories.length > 1 || (!isBookmarksView && categories.length > 0);

  return (
    <Flex className="flex-col gap-6" data-name="filters">
      {!isMobile && (
        <div data-name="tag-list">
          <SearchBar />
        </div>
      )}

      {showCategories && (
        <div className="rounded-2xl border border-space-15 p-6">
          <Text as="h2" variant="typo-lg-semibold">
            {t('articleTopics.title')}
          </Text>
          <div className="mt-4 mb-2 border-t border-space-15" />
          <Flex className="flex-col">
            {categories.map((category) => (
              <Link
                key={`category-${category.id}`}
                href={`/${market.prefix}${UrlHelper.advice}/${category.value}`}
                onClick={onTagClick(category.value)}
                className={cn(
                  'py-3 font-semibold text-space transition-colors hover:text-indigo-crush',
                  (activeSlug ? activeSlug === category.value : category.value.length === 0) &&
                    'text-indigo-crush',
                )}>
                {category.label}
              </Link>
            ))}
          </Flex>
        </div>
      )}
    </Flex>
  );
};

export default memo(ArticleCategoriesList);
